import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../core/network/api';
import { recipientKeys } from '../queries/keys';
import { recipientPaths } from '../queries/paths';

// Server-state hooks para receptoras (api-reference/admin-recipients.md).
// Entidad liviana: el alta basta con `number` (único entre activas).

// Página por defecto del listado (contrato: limit def 50 / máx 500).
const LIST_LIMIT = 50;

export function useRecipientsList(filters, opts = {}) {
    return useQuery({
        queryKey: recipientKeys.list(filters),
        queryFn:  ({ signal }) => api.get(recipientPaths.list(), { params: filters, signal }).then(r => r.data),
        placeholderData: (prev) => prev,
        ...opts,
    });
}

// Listado paginado "cargar más" (offset). Páginas acumuladas en TanStack Query —
// vite/03 [RIGID]. Reemplaza el limit:500 + aviso de truncado del overview.
export function useRecipientsInfinite(filters, limit = LIST_LIMIT) {
    return useInfiniteQuery({
        queryKey: recipientKeys.infinite(filters, limit),
        queryFn:  ({ pageParam, signal }) =>
            api.get(recipientPaths.list(), { params: { ...filters, limit, offset: pageParam }, signal }).then(r => r.data),
        initialPageParam: 0,
        getNextPageParam: (last) => {
            const p = last?.pagination;
            if (!p) return undefined;
            const next = p.offset + p.limit;
            return next < p.total ? next : undefined;
        },
        placeholderData: (prev) => prev,
    });
}

// Receptora ensamblada. view=full = dashboard (stats + current + history con
// cruce/outcome + medical_events, admin-recipients.md §0041).
export function useRecipient(id, view) {
    return useQuery({
        queryKey: ['recipients', 'detail', id, view || 'summary'],
        queryFn:  ({ signal }) => api.get(recipientPaths.detail(id), { params: view ? { view } : undefined, signal }).then(r => r.data),
        enabled:  !!id,
    });
}

export function useCreateRecipient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.post(recipientPaths.list(), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: recipientKeys.all }),
    });
}

export function useUpdateRecipient(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload) => api.patch(recipientPaths.detail(id), payload).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: recipientKeys.all }),
    });
}

export function useDeleteRecipient() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api.delete(recipientPaths.detail(id)).then(r => r.data),
        onSuccess:  () => qc.invalidateQueries({ queryKey: recipientKeys.all }),
    });
}
